import { cn } from '@/lib/utils';

/**
 * Cosmos brand mark — a constellation: one bright hub node (the platform) with
 * satellites linked to it (the modules).
 *
 * The plate is `bg-foreground` and the glyph `text-background`, the same trick
 * the old "AT" square used, so the mark inverts correctly in dark mode without
 * a second asset. Only the hub is drawn in brand cobalt.
 *
 * Keep the geometry here in sync with `public/icon.svg` (the favicon/PWA source),
 * which is the same drawing at 16x scale on a dark plate.
 */

/** Node + link geometry on a 32x32 grid. Lines are drawn first so the hub covers their origins. */
const LINKS = [
  { x1: 15, y1: 16.5, x2: 6.5,  y2: 8.5  },
  { x1: 15, y1: 16.5, x2: 24.5, y2: 6.5  },
  { x1: 15, y1: 16.5, x2: 26.5, y2: 21.5 },
  { x1: 15, y1: 16.5, x2: 11.5, y2: 26.5 },
  // satellite-to-satellite, so it reads as a constellation rather than a starburst
  { x1: 24.5, y1: 6.5, x2: 26.5, y2: 21.5 },
];

const SATELLITES = [
  { cx: 6.5,  cy: 8.5,  r: 2   },
  { cx: 24.5, cy: 6.5,  r: 2.2 },
  { cx: 26.5, cy: 21.5, r: 1.8 },
  { cx: 11.5, cy: 26.5, r: 1.7 },
];

export function CosmosGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" className={cn('text-background', className)} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5">
        {LINKS.map((l, i) => <line key={i} {...l} />)}
      </g>
      <g fill="currentColor">
        {SATELLITES.map((s, i) => <circle key={i} {...s} />)}
      </g>
      <circle cx="15" cy="16.5" r="3.8" className="fill-brand" />
    </svg>
  );
}

/** The glyph on its rounded plate. `size` is the plate edge in px. */
export function CosmosMark({ size = 28, className }: { size?: number; className?: string }) {
  return (
    <span
      className={cn('grid shrink-0 place-items-center rounded-md bg-foreground', className)}
      style={{ width: size, height: size }}
    >
      <CosmosGlyph className="h-[78%] w-[78%]" />
    </span>
  );
}

/**
 * Mark + "Cosmos" wordmark. `textClassName` carries the type scale so callers can
 * match their surrounding layout (header uses 14.5px, login 15px).
 */
export function CosmosWordmark({
  size = 28,
  textClassName = 'text-[14.5px]',
  className,
}: {
  size?: number;
  textClassName?: string;
  className?: string;
}) {
  return (
    <span className={cn('flex items-center gap-2', className)}>
      <CosmosMark size={size} />
      <span className={cn('font-semibold tracking-[-0.012em] text-foreground', textClassName)}>
        Cosmos
      </span>
    </span>
  );
}
